import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { RotateCw } from 'lucide-react';
import { motion } from 'framer-motion';
import delay from 'delay';

import { FileExtension } from '../../components/ReactFlow/tiptap/FileTTComponent';
import { ResizableMediaExtension } from '../../components/ReactFlow/tiptap/resizableMedia/ResizableMediaExtension';
import { StudentDBlockExtension } from '../../components/ReactFlow/tiptap/simulation/dBlock/StudentDBlockExtension';
import { useGetPersonaById } from '../../components/ReactFlow/tiptap/simulation/dBlock/useGetPersonaById';
import { withGrouping } from '../../components/ReactFlow/tiptap/simulation/dBlock/conversationGrouping';
import { ConversationPlaybackControl } from '../../components/ReactFlow/tiptap/simulation/dBlock/ConversationPlaybackControl';
import { PreviewLanguagePicker } from '../../components/ReactFlow/tiptap/simulation/dBlock/PreviewLanguagePicker';
import { ConversationTypingIndicator } from '@duke-hq/ui/components/Conversation/ConversationTypingIndicator';
import { ConversationOptionButton } from '@duke-hq/ui/components/Conversation/ConversationOptionButton';
import { ConversationReplyInput } from '@duke-hq/ui/components/Conversation/ConversationReplyInput';
import { DEFAULT_CONVERSATION_PLAYBACK_SPEED } from '@duke-hq/shared-constants/conversationPlaybackSpeeds';
import {
  PlaybackSpeed,
  getBlockAnimationDelayMS,
} from '../../components/ReactFlow/tiptap/simulation/dBlock/conversationPlayback';
import { ReplyComponent } from '../../components/ReactFlow/tiptap/simulation/replyComponent';
import { StudentQuizOptionExtension } from '../../components/ReactFlow/tiptap/simulation/StudentQuizOptionExtension';
import { Link } from '../../components/tiptap';
import { ConversationEndMessage } from '@duke-hq/ui/components/Conversation/ConversationEndMessage';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { useConversationAutoScroll } from '@duke-hq/ui/hooks/useConversationAutoScroll';
import {
  ENGLISH_LANGUAGE_CODE,
  isRtlLanguage,
  languageDir,
} from '@duke-hq/shared-constants/courseTranslationLanguages';
import { useReadyByLanguage } from '../../components/translations/useReadyByLanguage';

import type { PreviewVersion, PreviewLanguageControls } from './PreviewModal';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';

type LearningMaterialPreviewProps = {
  resourceId: string;
  lastPublishedVersionId: number | null;
  version: PreviewVersion;
  languageControls?: PreviewLanguageControls;
  versionToggle?: ReactNode;
};

export type FlowContent = {
  nodes: FlowNode[];
  edges: FlowEdge[];
};

type FlowNode = {
  id: string;
  type: string;
  data: {
    label?: string;
    tiptap: JSONContent;
    outgoers?: string[];
    options?: { id: string; title: string }[];
    reply?: string;
    quizId?: string;
  };
};

type FlowEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
};

export function isFlowContent(value: unknown): value is FlowContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'nodes' in value &&
    Array.isArray(value.nodes) &&
    'edges' in value &&
    Array.isArray(value.edges)
  );
}

type FlowPreviewLanguageControls = {
  value: string;
  onChange: (language: string) => void;
  readyByLanguage?: ReadonlyMap<string, boolean>;
};

export function ConversationFlowPreview({
  content,
  language,
  versionToggle,
  // The modal caps the scroll area so it doesn't outgrow the dialog; the canvas
  // panel already provides its own scroll, so it passes "none" to fill instead.
  maxHeight = '60vh',
}: {
  content: FlowContent;
  language?: FlowPreviewLanguageControls;
  versionToggle?: ReactNode;
  maxHeight?: string;
}) {
  const selectedLanguage = language?.value ?? ENGLISH_LANGUAGE_CODE;
  const isRtl = isRtlLanguage(selectedLanguage);
  const { nodes, edges } = content;

  const nodeMap = useMemo(() => {
    const map = new Map<string, FlowNode>();
    for (const node of nodes ?? []) {
      map.set(node.id, node);
    }
    return map;
  }, [nodes]);
  const nodeMapRef = useRef(nodeMap);
  nodeMapRef.current = nodeMap;

  const edgesBySource = useMemo(() => {
    const map = new Map<string, FlowEdge[]>();
    for (const edge of edges ?? []) {
      const existing = map.get(edge.source) ?? [];
      existing.push(edge);
      map.set(edge.source, existing);
    }
    return map;
  }, [edges]);
  const edgesBySourceRef = useRef(edgesBySource);
  edgesBySourceRef.current = edgesBySource;

  const startNodeId = useMemo(() => (nodes ?? []).find((n) => n.type === 'start')?.id, [nodes]);

  // -- State ------------------------------------------------------

  const [currentOptions, setCurrentOptions] = useState<{
    nodeId: string;
    options: { id: string; title: string }[];
  } | null>(null);
  const [replyNodeId, setReplyNodeId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [ended, setEnded] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState<PlaybackSpeed>(
    DEFAULT_CONVERSATION_PLAYBACK_SPEED,
  );

  const videoPlayingRef = useRef(false);
  const startedRef = useRef(false);
  const traversalIdRef = useRef(0);
  const simulationSpeedRef = useRef(simulationSpeed);
  simulationSpeedRef.current = simulationSpeed;

  const { getPersonaById, personasReady } = useGetPersonaById();
  const { containerRef, onSelectionUpdate } = useConversationAutoScroll<HTMLDivElement>();

  // -- Editor -----------------------------------------------------

  const editor = useEditor({
    onSelectionUpdate,
    extensions: [
      StudentQuizOptionExtension,
      ReplyComponent,
      FileExtension,
      ResizableMediaExtension.configure({
        onVideoComplete: () => {
          videoPlayingRef.current = false;
        },
        onPlaying: () => {
          videoPlayingRef.current = true;
        },
        autoPlay: true,
      }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      StudentDBlockExtension.configure({ getPersonaById }),
      StarterKit.configure({}),
      Underline,
    ],
    editable: false,
  });

  // -- Content helpers --------------------------------------------

  const editorRef = useRef(editor);
  editorRef.current = editor;

  const addContentToEditor = useCallback(async (newData: JSONContent, runId: number) => {
    const ed = editorRef.current;
    if (!ed || !newData) {
      return;
    }

    // A node's content is either a `doc` wrapping several blocks (a flow node's
    // tiptap) or a single block node (a reply echo). Either way, append the flat
    // list of top-level blocks as direct children of the editor doc - matching
    // the student runtime - so consecutive same-speaker dBlocks group correctly.
    const contentBlocks = newData.type === 'doc' ? (newData.content ?? []) : [newData];

    const buildSimulationBlock = (
      block: NonNullable<JSONContent['content']>[number],
      index: number,
      animate: boolean,
    ): JSONContent => ({
      ...block,
      attrs: {
        ...block.attrs,
        simulation: true,
        animation: animate,
        animationIndex: index,
      },
    });

    const appendBlock = (block: JSONContent) => {
      const root = ed.getJSON();
      const blocks = root.content === undefined || ed.isEmpty ? [block] : [...root.content, block];
      ed.commands.setContent({ type: 'doc', content: withGrouping(blocks) });
    };

    // In "skip animation" mode, append every block at once with no fade or delay,
    // so the conversation completes instantly while keeping whatever is already
    // on screen.
    if (simulationSpeedRef.current === 'instant') {
      setShowEllipsis(false);
      contentBlocks.forEach((block, index) =>
        appendBlock(buildSimulationBlock(block, index, false)),
      );
      return;
    }

    let index = 0;
    for (const block of contentBlocks) {
      if (traversalIdRef.current !== runId) {
        return;
      }

      setShowEllipsis(true);
      appendBlock(buildSimulationBlock(block, index, true));

      await delay(getBlockAnimationDelayMS(block, simulationSpeedRef.current));
      index++;
    }
  }, []);

  // -- Traversal --------------------------------------------------

  const getNextNodeId = useCallback((fromNodeId: string, optionId?: string): string | null => {
    const nodeEdges = edgesBySourceRef.current.get(fromNodeId) ?? [];

    if (optionId) {
      const optionEdge = nodeEdges.find((e) => e.sourceHandle === optionId);
      if (optionEdge) {
        return optionEdge.target;
      }
    }

    const defaultEdge = nodeEdges.find((e) => !e.sourceHandle) ?? nodeEdges[0];
    return defaultEdge?.target ?? null;
  }, []);

  const performTraversal = useCallback(
    async (nodeId: string, runId: number) => {
      const node = nodeMapRef.current.get(nodeId);
      if (!node || node.type === 'end' || traversalIdRef.current !== runId) {
        setEnded(true);
        setShowEllipsis(false);
        return;
      }

      if (node.data.tiptap) {
        await addContentToEditor(node.data.tiptap, runId);
      }

      if (traversalIdRef.current !== runId) {
        return;
      }

      const isInstant = simulationSpeedRef.current === 'instant';

      // Pause at options (in "show all at once" mode, auto-advance by taking the
      // first option so the whole conversation renders without input).
      if (node.data.options?.length) {
        if (isInstant) {
          const firstOption = node.data.options[0];
          const nextId = firstOption ? getNextNodeId(node.id, firstOption.id) : null;
          if (firstOption && nextId) {
            await addContentToEditor(
              { type: 'replyComponent', content: [{ type: 'text', text: firstOption.title }] },
              runId,
            );
            performTraversal(nextId, runId);
          } else {
            setEnded(true);
            setShowEllipsis(false);
          }
          return;
        }
        setCurrentOptions({ nodeId: node.id, options: node.data.options });
        setShowEllipsis(false);
        return;
      }

      // Pause at reply (auto-advance in instant mode).
      if (node.data.reply) {
        if (isInstant) {
          const replyNextId = getNextNodeId(node.id);
          if (replyNextId) {
            performTraversal(replyNextId, runId);
          } else {
            setEnded(true);
            setShowEllipsis(false);
          }
          return;
        }
        setReplyNodeId(node.id);
        setShowEllipsis(false);
        return;
      }

      // Auto-advance to next node
      const nextId = getNextNodeId(node.id);
      if (nextId) {
        if (!isInstant) {
          await delay(500);
        }
        performTraversal(nextId, runId);
      } else {
        setEnded(true);
        setShowEllipsis(false);
      }
    },
    [addContentToEditor, getNextNodeId],
  );

  const traversalFromStart = useCallback(() => {
    // Replaying always animates from the start. "Skip animation" is a one-shot,
    // so leave instant mode here, otherwise a replay would just re-render the
    // finished conversation instantly and look like nothing happened.
    if (simulationSpeedRef.current === 'instant') {
      simulationSpeedRef.current = DEFAULT_CONVERSATION_PLAYBACK_SPEED;
      setSimulationSpeed(DEFAULT_CONVERSATION_PLAYBACK_SPEED);
    }

    const runId = ++traversalIdRef.current;
    setEnded(false);
    if (startNodeId) {
      performTraversal(startNodeId, runId);
    }
  }, [startNodeId, performTraversal]);

  const restartSimulation = useCallback(() => {
    videoPlayingRef.current = false;
    editorRef.current?.commands.clearContent();
    setCurrentOptions(null);
    setReplyNodeId(null);
    setReplyText('');
    setShowEllipsis(false);
    traversalFromStart();
  }, [traversalFromStart]);

  // -- Option / reply handlers ------------------------------------

  const getNodeFromOption = useCallback(
    async (optionId: string, optionTitle: string) => {
      if (!currentOptions) {
        return;
      }

      const nodeEdges = edgesBySourceRef.current.get(currentOptions.nodeId) ?? [];
      const edgeFromOption = nodeEdges.find((e) => e.sourceHandle === optionId);

      if (edgeFromOption) {
        setCurrentOptions(null);

        const runId = traversalIdRef.current;
        // Await the echoed reply before traversing on, otherwise both append to
        // the editor concurrently and the messages land out of order.
        await addContentToEditor(
          { type: 'replyComponent', content: [{ type: 'text', text: optionTitle }] },
          runId,
        );

        performTraversal(edgeFromOption.target, runId);
      }
    },
    [currentOptions, addContentToEditor, performTraversal],
  );

  const addReply = useCallback(async () => {
    if (!replyNodeId || !replyText.trim()) {
      return;
    }

    const savedReplyNodeId = replyNodeId;
    setReplyNodeId(null);

    await addContentToEditor(
      { type: 'replyComponent', content: [{ type: 'text', text: replyText }] },
      traversalIdRef.current,
    );

    setReplyText('');

    const nextId = getNextNodeId(savedReplyNodeId);
    if (nextId) {
      performTraversal(nextId, traversalIdRef.current);
    }
  }, [replyNodeId, replyText, addContentToEditor, getNextNodeId, performTraversal]);

  // Switching to "skip animation" while the preview is paused at a fork or a
  // reply prompt resumes the run, so the rest of the conversation renders
  // instead of waiting on input that "skip" is meant to bypass.
  const handleSpeedChange = useCallback(
    (next: PlaybackSpeed) => {
      setSimulationSpeed(next);
      // Drive the rest of the resume off the new value now, before the re-render
      // that syncs the ref, so the traversal below runs in instant mode.
      simulationSpeedRef.current = next;
      if (next !== 'instant') {
        return;
      }
      const runId = traversalIdRef.current;
      if (currentOptions) {
        const firstOption = currentOptions.options[0];
        const nextId = firstOption ? getNextNodeId(currentOptions.nodeId, firstOption.id) : null;
        setCurrentOptions(null);
        if (firstOption && nextId) {
          addContentToEditor(
            { type: 'replyComponent', content: [{ type: 'text', text: firstOption.title }] },
            runId,
          );
          performTraversal(nextId, runId);
        } else {
          setEnded(true);
          setShowEllipsis(false);
        }
      } else if (replyNodeId) {
        const nextId = getNextNodeId(replyNodeId);
        setReplyNodeId(null);
        if (nextId) {
          performTraversal(nextId, runId);
        } else {
          setEnded(true);
          setShowEllipsis(false);
        }
      }
    },
    [currentOptions, replyNodeId, getNextNodeId, addContentToEditor, performTraversal],
  );

  // -- Start traversal when editor is ready -----------------------

  useEffect(() => {
    // Wait for personas before starting, otherwise the first block renders with
    // the Duke fallback and won't re-render once the persona resolves.
    if (editor && !startedRef.current && startNodeId && personasReady) {
      startedRef.current = true;
      traversalFromStart();
    }
  }, [editor, startNodeId, traversalFromStart, personasReady]);

  // -- Render -----------------------------------------------------

  if (!startNodeId || !nodes?.length) {
    // Keep the toggle and language picker reachable so the author isn't stranded
    // on an empty language and can switch back, matching the with-content header.
    return (
      <Box maxW="800px" mx="auto">
        <Flex align="center" justify={versionToggle ? 'space-between' : 'center'} gap="3" mb="4">
          {versionToggle}
          <ConversationPlaybackControl
            speed={simulationSpeed}
            onChange={handleSpeedChange}
            onRestart={restartSimulation}
            language={language}
          />
        </Flex>
        <Box textAlign="center" py="8">
          <Text color="fg.muted">
            <Trans>No conversation content available.</Trans>
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW="800px" mx="auto">
      {/* Canvas has no toggle, so centre the controls instead of edge-splitting. */}
      <Flex align="center" justify={versionToggle ? 'space-between' : 'center'} gap="3" mb="4">
        {versionToggle}
        <ConversationPlaybackControl
          speed={simulationSpeed}
          onChange={handleSpeedChange}
          onRestart={restartSimulation}
          language={language}
        />
      </Flex>

      <Flex
        ref={containerRef}
        direction="column"
        maxH={maxHeight}
        overflowY="auto"
        overflowX="hidden"
        gap="8px"
        p="4"
        bg="bg.muted"
        borderRadius="lg"
        dir={languageDir(selectedLanguage)}
        // `containerType` makes this a query container so the preview's responsive
        // content sizes against the preview width, not the admin viewport.
        sx={{
          containerType: 'inline-size',
          // Spacing between message bubbles, matching the student runtime.
          '& .tiptap > * + *': { marginTop: 'var(--chakra-space-1)' },
        }}
      >
        {editor && !editor.isEmpty ? (
          <EditorContent style={{ width: '100%' }} editor={editor} />
        ) : null}

        {currentOptions?.options.map((option, i) => (
          <motion.div
            key={option.id}
            style={{ alignSelf: isRtl ? 'flex-start' : 'flex-end', maxWidth: '70%' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: i / 2, ease: [0, 0.71, 0.2, 1.01] }}
          >
            <ConversationOptionButton
              isRtl={isRtl}
              onClick={() => getNodeFromOption(option.id, option.title)}
            >
              {option.title}
            </ConversationOptionButton>
          </motion.div>
        ))}

        <ConversationTypingIndicator
          visible={showEllipsis}
          speed={simulationSpeed === 'instant' ? undefined : simulationSpeed}
        />

        {ended ? (
          <ConversationEndMessage
            action={
              <Button
                sx={{ width: '100%', '@container (min-width: 48em)': { width: 'fit-content' } }}
                leftIcon={<RotateCw size={iconSize.sm} />}
                onClick={restartSimulation}
                mt="2"
                variant="outline"
              >
                Replay conversation
              </Button>
            }
          />
        ) : null}
      </Flex>

      {replyNodeId ? (
        <ConversationReplyInput
          value={replyText}
          onChange={setReplyText}
          onSubmit={addReply}
          placeholder={t`Send a message...`}
        />
      ) : null}
    </Box>
  );
}

export function LearningMaterialPreview({
  resourceId,
  lastPublishedVersionId,
  version,
  languageControls,
  versionToggle,
}: LearningMaterialPreviewProps) {
  const language = languageControls?.value ?? ENGLISH_LANGUAGE_CODE;
  const showPublished = version === 'published' && lastPublishedVersionId !== null;
  const showTranslated = showPublished && language !== ENGLISH_LANGUAGE_CODE;

  const resourceQuery = trpcAdmin.resources.byId.useQuery(resourceId, {
    enabled: !showPublished,
  });

  // Also enabled when translated, to back the English fallback.
  const publishedVersionQuery = trpcAdmin.resourceVersions.byId.useQuery(
    lastPublishedVersionId ?? 0,
    { enabled: showPublished },
  );

  // Availability per language, so the picker can grey out ones that aren't ready.
  const readyByLanguage = useReadyByLanguage(
    lastPublishedVersionId,
    'CONVERSATION',
    languageControls !== undefined,
  );

  // The same read path the student uses. Null content means "serve English".
  const translatedQuery = trpcAdmin.contentTranslations.getTranslatedContent.useQuery(
    { publishedVersionId: lastPublishedVersionId ?? 0, resourceKind: 'CONVERSATION', language },
    { enabled: showTranslated },
  );

  const content = showTranslated
    ? (translatedQuery.data?.content ?? publishedVersionQuery.data?.content)
    : showPublished
      ? publishedVersionQuery.data?.content
      : resourceQuery.data?.draftVersion?.content;

  const isLoading = showTranslated
    ? translatedQuery.isLoading || publishedVersionQuery.isLoading
    : showPublished
      ? publishedVersionQuery.isLoading
      : resourceQuery.isLoading;

  const flowLanguageControls =
    languageControls === undefined ? undefined : { ...languageControls, readyByLanguage };

  // Loading/empty states have no playback controls, so render just the toggle and
  // picker here to keep Published/Draft reachable. With content, the row lives in
  // ConversationFlowPreview.
  if (isLoading || !content || !isFlowContent(content)) {
    return (
      <Box maxW="800px" mx="auto">
        <Flex align="center" justify="space-between" gap="3" mb="4">
          {versionToggle ?? <Box />}
          {flowLanguageControls ? (
            <PreviewLanguagePicker
              value={flowLanguageControls.value}
              onChange={flowLanguageControls.onChange}
              readyByLanguage={flowLanguageControls.readyByLanguage}
            />
          ) : null}
        </Flex>
        {isLoading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner />
          </Flex>
        ) : (
          <Box textAlign="center" py="8">
            <Text color="fg.muted">
              <Trans>No content available for this version.</Trans>
            </Text>
          </Box>
        )}
      </Box>
    );
  }

  const fileUrls = showTranslated
    ? translatedQuery.data?.content != null
      ? translatedQuery.data.fileUrls
      : publishedVersionQuery.data?.fileUrls
    : showPublished
      ? publishedVersionQuery.data?.fileUrls
      : resourceQuery.data?.fileUrls;

  return (
    <FileUrlsProvider fileUrls={fileUrls}>
      <ConversationFlowPreview
        content={content}
        language={flowLanguageControls}
        versionToggle={versionToggle}
      />
    </FileUrlsProvider>
  );
}
